<template>
  <div class="p-6">
    <!-- Header Premium -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
      <div>
        <div class="flex items-center gap-3 mb-1">
          <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-200 dark:shadow-none">
            <Icon icon="mdi:application-settings" class="text-white text-xl" />
          </div>
          <h1 class="text-2xl font-black text-gray-900 dark:text-white tracking-tight">Gestión de Módulos</h1>
        </div>
        <p class="text-sm font-medium text-gray-500 dark:text-gray-400 ml-13">Administre la estructura y accesibilidad de los módulos del sistema</p>
      </div>

      <div class="flex items-center gap-3 w-full md:w-auto">
        <div class="relative group flex-1 md:flex-none">
          <button
            @click="showExportMenu = !showExportMenu"
            class="w-full md:w-auto flex items-center justify-center px-5 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-bold text-sm hover:border-purple-500 hover:text-purple-600 transition-all shadow-sm"
          >
            <Icon icon="mdi:printer" class="mr-2 text-lg" />
            Imprimir
            <Icon icon="mdi:chevron-down" class="ml-2 opacity-50" />
          </button>

          <transition enter-active-class="transition duration-200 ease-out" enter-from-class="transform scale-95 opacity-0" enter-to-class="transform scale-100 opacity-100" leave-active-class="transition duration-150 ease-in" leave-from-class="transform scale-100 opacity-100" leave-to-class="transform scale-95 opacity-0">
            <div v-if="showExportMenu"
                 class="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 z-[60] overflow-hidden">
              <div class="p-2">
                <button @click="printFullReport" class="w-full px-4 py-3 text-left hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-xl flex items-center transition-all group">
                  <div class="p-2 bg-red-50 dark:bg-red-900/30 rounded-lg mr-3 group-hover:scale-110 transition-transform">
                    <Icon icon="mdi:file-pdf-box" class="text-red-600 text-xl" />
                  </div>
                  <div>
                    <p class="font-black text-[10px] uppercase tracking-wider text-gray-400">PDF COMPLETO</p>
                    <p class="text-sm font-bold text-gray-700 dark:text-gray-300">Reporte Estructural</p>
                  </div>
                </button>
                <div class="h-px bg-gray-100 dark:bg-gray-700 my-1 mx-2"></div>
                <button @click="printSimplifiedReport" class="w-full px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-xl flex items-center transition-all group">
                  <div class="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg mr-3 group-hover:scale-110 transition-transform">
                    <Icon icon="mdi:table" class="text-blue-600 text-xl" />
                  </div>
                  <div>
                    <p class="font-black text-[10px] uppercase tracking-wider text-gray-400">LISTADO</p>
                    <p class="text-sm font-bold text-gray-700 dark:text-gray-300">Tabla Simplificada</p>
                  </div>
                </button>
              </div>
            </div>
          </transition>
        </div>

        <button
          @click="openOrganizarOrdenModal"
          class="flex-1 md:flex-none flex items-center justify-center px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl font-black text-sm hover:shadow-lg hover:shadow-indigo-200 dark:hover:shadow-none hover:translate-y-[-1px] transition-all gap-2"
          title="Organizar qué módulo va primero y el orden de los submódulos"
        >
          <Icon icon="mdi:sort-variant" class="text-xl" />
          ORGANIZAR MENÚ
        </button>

        <button
          @click="openCreateModal"
          class="flex-1 md:flex-none flex items-center justify-center px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-black text-sm hover:shadow-lg hover:shadow-purple-200 dark:hover:shadow-none hover:translate-y-[-1px] transition-all"
        >
          <Icon icon="mdi:plus" class="mr-2 text-xl" />
          NUEVO MÓDULO
        </button>
      </div>
    </div>

    <!-- Panel de Filtros Premium -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl shadow-gray-100 dark:shadow-none p-6 mb-8 border border-gray-100 dark:border-gray-700 relative overflow-hidden">
      <div class="absolute top-0 left-0 w-1 h-full bg-purple-500"></div>

      <div class="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div class="md:col-span-12 lg:col-span-5">
          <label class="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Explorador de Módulos</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl group-focus-within:text-purple-500 transition-colors" />
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Nombre, ruta o descripción..."
              class="w-full pl-12 pr-4 py-3 bg-gray-50/50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-purple-500 text-sm font-medium transition-all text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400"
              @input="handleSearch"
            />
          </div>
        </div>

        <div class="md:col-span-4 lg:col-span-2">
          <label class="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Nivel</label>
          <select
            v-model="filterTipo"
            class="w-full px-4 py-3 bg-gray-50/50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-purple-500 text-sm font-bold appearance-none cursor-pointer text-gray-900 dark:text-white"
          >
            <option value="all">Todos los niveles</option>
            <option value="raiz">Solo raíces</option>
            <option value="conPadre">Solo hijos</option>
            <option value="tipo_modulo">Tipo: módulo</option>
            <option value="tipo_submodulo">Tipo: submódulo</option>
          </select>
        </div>

        <div class="md:col-span-4 lg:col-span-2">
          <label class="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Estado</label>
          <select
            v-model="filterStatus"
            class="w-full px-4 py-3 bg-gray-50/50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-purple-500 text-sm font-bold appearance-none cursor-pointer text-gray-900 dark:text-white"
            @change="handleFilterChange"
          >
            <option value="all">Todos</option>
            <option value="activos">Activos</option>
            <option value="inactivos">Inactivos</option>
          </select>
        </div>

        <div class="md:col-span-4 lg:col-span-3">
          <label class="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Ordenamiento</label>
          <select
            v-model="sortBy"
            class="w-full px-4 py-3 bg-gray-50/50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-purple-500 text-sm font-bold appearance-none cursor-pointer text-gray-900 dark:text-white"
          >
            <option value="orden">Orden (#)</option>
            <option value="nombre">Nombre (A-Z)</option>
            <option value="nombre_desc">Nombre (Z-A)</option>
            <option value="ruta">Ruta (Directorio)</option>
            <option value="fechaCreacion_desc">Más recientes</option>
          </select>
        </div>

        <div class="md:col-span-4 lg:col-span-2 flex items-end">
          <button
            @click="loadModulos"
            class="w-full h-[44px] flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-xl hover:bg-purple-500 hover:text-white transition-all group"
            title="Reiniciar lista"
          >
            <Icon icon="mdi:refresh" class="text-xl group-hover:rotate-180 transition-transform duration-500" :class="{ 'animate-spin': loading }" />
            <span class="ml-2 font-bold text-xs">ACTUALIZAR</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Estadísticas Premium -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <div class="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-xl shadow-gray-100/50 dark:shadow-none transition-all hover:scale-[1.02]">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center">
            <Icon icon="mdi:application-settings" class="text-purple-600 dark:text-purple-400 text-2xl" />
          </div>
          <div>
            <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Sistema</p>
            <h3 class="text-2xl font-black text-gray-900 dark:text-white">{{ modulos.length }}</h3>
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-xl shadow-gray-100/50 dark:shadow-none transition-all hover:scale-[1.02]">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
            <Icon icon="mdi:check-decagram" class="text-emerald-600 dark:text-emerald-400 text-2xl" />
          </div>
          <div>
            <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Activos</p>
            <h3 class="text-2xl font-black text-emerald-600 dark:text-emerald-400">{{ estadisticas.activos }}</h3>
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-xl shadow-gray-100/50 dark:shadow-none transition-all hover:scale-[1.02]">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
            <Icon icon="mdi:folder-multiple" class="text-blue-600 dark:text-blue-400 text-2xl" />
          </div>
          <div>
            <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Módulos Raíz</p>
            <h3 class="text-2xl font-black text-blue-600 dark:text-blue-400">{{ estadisticas.raices }}</h3>
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-xl shadow-gray-100/50 dark:shadow-none transition-all hover:scale-[1.02]">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center">
            <Icon icon="mdi:file-tree" class="text-indigo-600 dark:text-indigo-400 text-2xl" />
          </div>
          <div>
            <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Submódulos</p>
            <h3 class="text-2xl font-black text-indigo-600 dark:text-indigo-400">{{ estadisticas.submodulos }}</h3>
          </div>
        </div>
      </div>
    </div>

    <!-- Indicadores de filtro -->
    <div v-if="isFiltered" class="mb-4">
      <div class="flex flex-wrap items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
        <span class="text-sm font-medium text-amber-800 dark:text-amber-300">
          <Icon icon="mdi:filter" class="inline mr-1" />
          Filtros aplicados:
        </span>
        <span v-if="searchQuery" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
          Buscar: "{{ searchQuery }}"
          <button @click="searchQuery = ''; handleSearch()" class="ml-1.5 hover:text-blue-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <span v-if="filterTipo !== 'all'" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200">
          Nivel: {{ filterTipoLabel }}
          <button @click="filterTipo = 'all'" class="ml-1.5 hover:text-indigo-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <span v-if="filterStatus !== 'all'" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200">
          Estado: {{ filterStatus === 'activos' ? 'Activos' : 'Inactivos' }}
          <button @click="filterStatus = 'all'; handleFilterChange()" class="ml-1.5 hover:text-indigo-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <button
          @click="clearFilters"
          class="text-sm text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-300 font-medium"
        >
          <Icon icon="mdi:filter-off" class="inline mr-1" />
          Limpiar filtros
        </button>
      </div>
    </div>

    <!-- Tabla de módulos -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
      <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <div>
          <h2 class="text-lg font-semibold text-gray-800 dark:text-white">Lista de Módulos</h2>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Mostrando {{ filteredModulos.length }} de {{ modulos.length }} módulos
          </p>
        </div>
        <div class="flex items-center space-x-2">
          <div class="text-sm text-gray-500 dark:text-gray-400">
            <Icon icon="mdi:calendar" class="inline mr-1" />
            Última actualización: {{ formatTime(lastUpdate) }}
          </div>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <div class="flex items-center space-x-1">
                  <span>Módulo</span>
                  <button @click="sortBy = sortBy === 'nombre' ? 'nombre_desc' : 'nombre'">
                    <Icon icon="mdi:sort-alphabetical" class="text-gray-400 hover:text-gray-600" />
                  </button>
                </div>
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Padre</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ruta</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Orden</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Tipo</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Estado</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-if="loading">
              <td colspan="7" class="px-6 py-12">
                <div class="flex flex-col items-center justify-center">
                  <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
                  <p class="text-gray-600 dark:text-gray-400">Cargando módulos...</p>
                </div>
              </td>
            </tr>

            <tr v-else-if="filteredModulos.length === 0">
              <td colspan="7" class="px-6 py-16 text-center">
                <div class="flex flex-col items-center">
                  <div class="p-4 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                    <Icon icon="mdi:application-remove" class="text-3xl text-gray-400" />
                  </div>
                  <h3 class="text-lg font-medium text-gray-800 dark:text-white mb-2">
                    No se encontraron módulos
                  </h3>
                  <p class="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
                    {{ searchQuery || filterStatus !== 'all' || filterTipo !== 'all'
                      ? 'Intenta con otros términos de búsqueda o cambia los filtros.'
                      : 'No hay módulos registrados. ¡Crea el primero!' }}
                  </p>
                  <button
                    v-if="!searchQuery && filterStatus === 'all' && filterTipo === 'all'"
                    @click="openCreateModal"
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Icon icon="mdi:plus" class="mr-2 inline" />
                    Crear primer módulo
                  </button>
                  <button
                    v-else
                    @click="clearFilters"
                    class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    <Icon icon="mdi:filter-off" class="mr-2 inline" />
                    Limpiar filtros
                  </button>
                </div>
              </td>
            </tr>

            <tr
              v-else
              v-for="modulo in paginatedModulos"
              :key="modulo.idModulo"
              class="group hover:bg-gray-50/80 dark:hover:bg-gray-700/30 transition-all duration-300 border-b border-gray-100 dark:border-gray-700/50 last:border-none"
              @click="openDetailModal(modulo)"
            >
              <td class="px-6 py-5">
                <div class="flex items-center gap-4" :style="{ paddingLeft: (getNivel(modulo) * 24) + 'px' }">
                  <Icon
                    v-if="getNivel(modulo) > 0"
                    icon="mdi:subdirectory-arrow-right"
                    class="text-blue-400 flex-shrink-0"
                    width="18"
                    height="18"
                  />
                  <div class="relative">
                    <div :class="[
                      'w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 shadow-sm',
                      modulo.estado?.toLowerCase() === 'activo'
                        ? 'bg-gradient-to-br from-purple-500/10 to-indigo-500/10 dark:from-purple-500/20 dark:to-indigo-500/20'
                        : 'bg-gray-100 dark:bg-gray-800'
                    ]">
                      <Icon
                        :icon="modulo.icono || 'mdi:folder-outline'"
                        :class="[
                          'text-2xl',
                          modulo.estado?.toLowerCase() === 'activo'
                            ? 'text-purple-600 dark:text-purple-400'
                            : 'text-gray-400'
                        ]"
                      />
                    </div>
                    <div v-if="modulo.estado?.toLowerCase() === 'activo'" class="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white dark:border-gray-800 shadow-sm"></div>
                  </div>
                  <div>
                    <div class="text-sm font-black text-gray-900 dark:text-white tracking-tight group-hover:text-purple-600 transition-colors">
                      {{ modulo.nombre }}
                    </div>
                    <div class="text-[10px] font-bold text-gray-400 dark:text-gray-500 flex items-center mt-0.5 uppercase tracking-tighter">
                      <Icon icon="mdi:identifier" class="mr-1 opacity-50" />
                      ID: {{ modulo.idModulo }}
                      <span v-if="getNivel(modulo) > 0" class="ml-2 px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded">
                        Nivel {{ getNivel(modulo) }}
                      </span>
                      <span v-if="contarHijos(modulo.idModulo) > 0" class="ml-2 px-2 py-0.5 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-bold rounded-lg text-[10px] inline-flex items-center gap-1 border border-purple-200 dark:border-purple-800/60" title="Contiene submódulos adentro">
                        <Icon icon="mdi:folder-multiple" class="text-xs" />
                        {{ contarHijos(modulo.idModulo) }} submódulos
                      </span>
                    </div>
                  </div>
                </div>
              </td>

              <td class="px-6 py-5">
                <span
                  v-if="modulo.idPadre"
                  class="inline-flex items-center px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg text-[10px] font-black uppercase tracking-tighter"
                >
                  <Icon icon="mdi:subdirectory-arrow-right" class="mr-1" />
                  {{ getNombrePadre(modulo.idPadre) }}
                </span>
                <span v-else class="text-[10px] text-gray-400 italic">— Raíz —</span>
              </td>

              <td class="px-6 py-5">
                <div class="inline-flex items-center px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-lg group-hover:bg-purple-50 dark:group-hover:bg-purple-900/20 transition-colors">
                  <Icon icon="mdi:link-variant" class="mr-1.5 text-xs text-gray-400" />
                  <span class="text-[11px] font-black text-gray-600 dark:text-gray-300 font-mono tracking-tighter">
                    {{ modulo.ruta || '/' }}
                  </span>
                </div>
              </td>

              <td class="px-6 py-5" @click.stop>
                <div class="inline-flex items-center gap-1 bg-gray-50 dark:bg-gray-900/60 p-1 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                  <button
                    @click.stop="moverOrdenRapido(modulo, -1)"
                    class="w-6 h-6 rounded-lg flex items-center justify-center hover:bg-purple-100 dark:hover:bg-purple-900/50 text-gray-500 hover:text-purple-600 transition-colors"
                    title="Subir posición (ir antes)"
                  >
                    <Icon icon="mdi:chevron-up" class="text-base" />
                  </button>
                  <span class="px-2 text-xs font-black text-gray-800 dark:text-gray-100 min-w-[28px] text-center">
                    #{{ modulo.orden ?? 0 }}
                  </span>
                  <button
                    @click.stop="moverOrdenRapido(modulo, 1)"
                    class="w-6 h-6 rounded-lg flex items-center justify-center hover:bg-purple-100 dark:hover:bg-purple-900/50 text-gray-500 hover:text-purple-600 transition-colors"
                    title="Bajar posición (ir después)"
                  >
                    <Icon icon="mdi:chevron-down" class="text-base" />
                  </button>
                </div>
              </td>

              <td class="px-6 py-5">
                <span :class="[
                  'px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-tighter',
                  modulo.tipo === 'submodulo'
                    ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400'
                    : 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400'
                ]">
                  {{ modulo.tipo || 'modulo' }}
                </span>
              </td>

              <td class="px-6 py-5 whitespace-nowrap">
                <button
                  @click.stop="toggleStatus(modulo)"
                  :class="[
                    'px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border-2',
                    modulo.estado?.toLowerCase() === 'activo'
                      ? 'bg-emerald-50 border-emerald-500/20 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'
                      : 'bg-red-50 border-red-500/20 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                  ]"
                >
                  {{ modulo.estado || 'INACTIVO' }}
                </button>
              </td>

              <td class="px-6 py-5 whitespace-nowrap text-right">
                <div class="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    @click.stop="openEditModal(modulo)"
                    class="p-2.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-xl hover:bg-blue-600 hover:text-white transition-all shadow-sm"
                    title="Editar Configuración"
                  >
                    <Icon icon="mdi:pencil-outline" class="text-xl" />
                  </button>
                  <button
                    @click.stop="confirmDelete(modulo)"
                    class="p-2.5 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-xl hover:bg-red-600 hover:text-white transition-all shadow-sm"
                    title="Eliminar Módulo"
                  >
                    <Icon icon="mdi:delete-outline" class="text-xl" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div v-if="filteredModulos.length > 0 && !loading" class="px-6 py-5 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 rounded-b-2xl">
        <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div class="text-sm font-medium text-gray-500 dark:text-gray-400">
            Mostrando <span class="text-gray-900 dark:text-white">{{ startItem }}-{{ endItem }}</span> de
            <span class="text-gray-900 dark:text-white">{{ filteredModulos.length }}</span> resultados
          </div>

          <div class="flex items-center space-x-1">
            <button
              @click="currentPage = Math.max(currentPage - 1, 1)"
              :disabled="currentPage === 1"
              class="w-10 h-10 flex items-center justify-center rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              <Icon icon="mdi:chevron-left" class="text-xl" />
            </button>

            <div class="flex items-center space-x-1">
              <button
                v-for="page in visiblePages"
                :key="page"
                @click="currentPage = page"
                :class="[
                  'w-10 h-10 flex items-center justify-center rounded-xl font-bold text-sm transition-all shadow-sm',
                  currentPage === page
                    ? 'bg-purple-600 text-white shadow-purple-200 dark:shadow-none translate-y-[-2px]'
                    : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-purple-300 hover:bg-purple-50 dark:hover:bg-gray-700'
                ]"
              >
                {{ page }}
              </button>
            </div>

            <button
              @click="currentPage = Math.min(currentPage + 1, totalPages)"
              :disabled="currentPage === totalPages"
              class="w-10 h-10 flex items-center justify-center rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              <Icon icon="mdi:chevron-right" class="text-xl" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Crear/Editar - RESPONSIVE -->
    <Teleport to="body">
      <transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div
          v-if="showModal"
          class="fixed inset-0 bg-black/70 flex items-center justify-center p-2 sm:p-4 z-[90] backdrop-blur-[2px] overflow-y-auto"
          @click.self="closeModal"
        >
          <div class="bg-white dark:bg-gray-800 rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-lg mx-auto my-auto overflow-hidden transform transition-all tracking-tight max-h-[95vh] sm:max-h-[90vh] flex flex-col">
            <!-- Header -->
            <div class="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-purple-600 to-indigo-600 flex-shrink-0">
              <div class="flex justify-between items-center text-white gap-2">
                <div class="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <div class="p-1.5 sm:p-2 bg-white/20 rounded-xl flex-shrink-0">
                    <Icon :icon="editingModulo ? 'mdi:pencil-box' : 'mdi:application-plus'" class="text-xl sm:text-2xl" />
                  </div>
                  <div class="min-w-0">
                    <h3 class="text-lg sm:text-xl font-black truncate">{{ editingModulo ? 'Editar' : 'Nuevo' }} Módulo</h3>
                    <p class="text-[10px] sm:text-xs text-purple-100 font-medium italic truncate">
                      {{ form.idPadre ? 'Se creará como submódulo' : 'Estructura fundamental del sistema' }}
                    </p>
                  </div>
                </div>
                <button
                  @click="closeModal"
                  class="p-1.5 sm:p-2 hover:bg-white/20 rounded-xl transition-colors flex-shrink-0"
                  aria-label="Cerrar"
                >
                  <Icon icon="mdi:close" class="text-xl sm:text-2xl" />
                </button>
              </div>
            </div>

            <!-- Form -->
            <form @submit.prevent="saveModulo" class="p-4 sm:p-6 space-y-4 sm:space-y-5 overflow-y-auto flex-1">
              <div>
                <label class="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 ml-1">Nombre Identificativo *</label>
                <input
                  v-model="form.nombre"
                  type="text"
                  required
                  placeholder="Ej: Inventario Central"
                  class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-purple-500 text-sm font-bold text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400"
                />
              </div>

              <div>
                <label class="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 ml-1">
                  Módulo Padre <span class="text-gray-400 normal-case italic">(opcional)</span>
                </label>
                <select
                  :value="form.idPadre === null || form.idPadre === undefined ? '' : form.idPadre"
                  @change="form.idPadre = $event.target.value === '' ? null : Number($event.target.value)"
                  class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-purple-500 text-sm font-bold appearance-none cursor-pointer"
                >
                  <option value="">— Ninguno (es un módulo raíz) —</option>
                  <option
                    v-for="mod in modulosPadreDisponibles"
                    :key="mod.idModulo"
                    :value="mod.idModulo"
                  >
                    {{ '— '.repeat(getNivel(mod)) }}{{ mod.nombre }}
                  </option>
                </select>
                <p class="text-[10px] text-gray-400 mt-1 ml-1">
                  <Icon icon="mdi:information-outline" class="inline mr-1" />
                  Puedes anidar en cualquier módulo. Los submódulos heredan su rama padre.
                </p>
              </div>

              <div>
                <label class="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 ml-1">Descripción de Funciones</label>
                <textarea
                  v-model="form.descripcion"
                  rows="2"
                  placeholder="¿Cuál es el propósito de este módulo?"
                  class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-purple-500 text-sm resize-none font-medium text-gray-600 dark:text-gray-400"
                ></textarea>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 ml-1">Ruta del Módulo *</label>
                  <div class="relative">
                    <span class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-mono text-xs">/</span>
                    <input
                      v-model="form.ruta"
                      type="text"
                      required
                      placeholder="ventas"
                      class="w-full pl-7 pr-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-purple-500 text-sm font-mono font-bold text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400"
                    />
                  </div>
                </div>

                <div>
                  <label class="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 ml-1">Identificador de Icono</label>

                  <div class="relative" v-click-outside="closeIconPicker">
                    <button
                      type="button"
                      @click="showIconPicker = !showIconPicker"
                      class="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-none rounded-xl hover:bg-gray-100 dark:hover:bg-gray-900 transition-all text-left"
                    >
                      <div class="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                        <Icon :icon="form.icono || 'mdi:folder-outline'" class="text-lg text-purple-600 dark:text-purple-400" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-[9px] font-black text-gray-400 uppercase tracking-widest">Icono</p>
                        <p class="text-xs font-mono font-bold text-gray-700 dark:text-gray-300 truncate">
                          {{ form.icono || 'mdi:folder-outline' }}
                        </p>
                      </div>
                      <Icon icon="mdi:chevron-down" class="text-lg text-gray-400 flex-shrink-0" />
                    </button>

                    <transition
                      enter-active-class="transition duration-200 ease-out"
                      enter-from-class="transform scale-95 opacity-0 -translate-y-2"
                      enter-to-class="transform scale-100 opacity-100 translate-y-0"
                      leave-active-class="transition duration-150 ease-in"
                      leave-from-class="transform scale-100 opacity-100 translate-y-0"
                      leave-to-class="transform scale-95 opacity-0 -translate-y-2"
                    >
                      <div
                        v-if="showIconPicker"
                        class="absolute left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 z-[120] overflow-hidden"
                      >
                        <div class="p-3 border-b border-gray-100 dark:border-gray-700">
                          <div class="relative">
                            <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                              v-model="iconSearch"
                              type="text"
                              placeholder="Buscar icono..."
                              class="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-gray-900/50 border-none rounded-lg text-sm font-medium focus:ring-2 focus:ring-purple-500 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400"
                              @click.stop
                            />
                          </div>
                        </div>

                        <div class="p-3 max-h-[240px] overflow-y-auto">
                          <div v-if="filteredIcons.length === 0" class="text-center py-8">
                            <Icon icon="mdi:emoticon-sad-outline" class="text-3xl text-gray-300 mx-auto mb-2" />
                            <p class="text-xs text-gray-400 font-bold">Sin resultados</p>
                          </div>

                          <div v-else class="grid grid-cols-6 gap-1">
                            <button
                              v-for="icon in filteredIcons"
                              :key="icon"
                              type="button"
                              @click="selectIcon(icon)"
                              :class="[
                                'aspect-square flex items-center justify-center rounded-xl transition-all group relative',
                                form.icono === icon
                                  ? 'bg-purple-600 text-white shadow-lg shadow-purple-200 dark:shadow-none scale-105'
                                  : 'bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:text-purple-600 hover:scale-105'
                              ]"
                              :title="icon"
                            >
                              <Icon :icon="icon" class="text-xl" />
                              <div v-if="form.icono === icon" class="absolute -top-1 -right-1 w-4 h-4 bg-white rounded-full flex items-center justify-center">
                                <Icon icon="mdi:check" class="text-purple-600 text-xs" />
                              </div>
                            </button>
                          </div>
                        </div>

                        <div class="px-3 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 flex items-center justify-between">
                          <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                            {{ filteredIcons.length }} iconos
                          </p>
                          <button
                            type="button"
                            @click="showIconPicker = false"
                            class="text-[10px] font-black text-purple-600 hover:text-purple-700 uppercase tracking-widest"
                          >
                            Cerrar
                          </button>
                        </div>
                      </div>
                    </transition>
                  </div>
                </div>
              </div>

              <div>
                <label class="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 ml-1">Orden de aparición</label>
                <input
                  v-model.number="form.orden"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-purple-500 text-sm font-bold text-gray-900 dark:text-white"
                />
                <p class="text-[10px] text-gray-400 mt-1 ml-1">Números menores aparecen primero.</p>
              </div>

              <div class="pt-2 border-t border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3 ml-1">Estado de Disponibilidad</label>
                <div class="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <button
                    type="button"
                    @click="form.estado = 'Activo'"
                    :class="[
                      'flex-1 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all border-2',
                      form.estado === 'Activo'
                        ? 'bg-emerald-50 border-emerald-500 text-emerald-700 dark:bg-emerald-900/20'
                        : 'bg-white border-gray-100 text-gray-400 dark:bg-gray-800 dark:border-gray-700'
                    ]"
                  >
                    Módulo Activo
                  </button>
                  <button
                    type="button"
                    @click="form.estado = 'Inactivo'"
                    :class="[
                      'flex-1 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all border-2',
                      form.estado === 'Inactivo'
                        ? 'bg-red-50 border-red-500 text-red-700 dark:bg-red-900/20'
                        : 'bg-white border-gray-100 text-gray-400 dark:bg-gray-800 dark:border-gray-700'
                    ]"
                  >
                    Fuera de Servicio
                  </button>
                </div>
              </div>

              <div class="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                <button
                  type="button"
                  @click="closeModal"
                  class="flex-1 px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 font-bold text-sm hover:bg-gray-50 transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  :disabled="saving || !isFormValid"
                  class="flex-[2] px-4 py-3 bg-purple-600 text-white rounded-xl font-black text-sm hover:bg-purple-700 shadow-lg shadow-purple-200 dark:shadow-none transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Icon v-if="saving" icon="mdi:loading" class="animate-spin text-xl" />
                  <Icon v-else :icon="editingModulo ? 'mdi:check-bold' : 'mdi:plus-thick'" class="text-xl" />
                  {{ saving ? 'PROCESANDO...' : (editingModulo ? 'ACTUALIZAR DATOS' : 'CREAR MÓDULO') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- Modal de detalles - RESPONSIVE -->
    <Teleport to="body">
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="opacity-0 scale-95" enter-to-class="opacity-100 scale-100" leave-active-class="transition duration-200 ease-in" leave-from-class="opacity-100 scale-100" leave-to-class="opacity-0 scale-95">
        <div
          v-if="showDetailModal"
          class="fixed inset-0 bg-black/70 flex items-center justify-center p-2 sm:p-4 z-[90] backdrop-blur-[2px] overflow-y-auto"
          @click.self="closeDetailModal"
        >
          <div class="bg-white dark:bg-gray-800 rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-md mx-auto my-auto overflow-hidden transform transition-all max-h-[95vh] overflow-y-auto">
            <div class="relative h-28 sm:h-32 bg-gradient-to-br from-purple-600 to-indigo-700 overflow-hidden">
              <div class="absolute inset-0 opacity-10">
                <Icon icon="mdi:application" class="text-[12rem] -translate-x-10 -translate-y-10 rotate-12" />
              </div>
              <button @click="closeDetailModal" class="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors text-white z-10">
                <Icon icon="mdi:close" class="text-xl" />
              </button>
              <div class="absolute -bottom-10 left-6 sm:left-8">
                <div class="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white dark:bg-gray-800 flex items-center justify-center shadow-2xl border-4 border-white dark:border-gray-800">
                  <Icon :icon="selectedModulo?.icono || 'mdi:folder'" class="text-3xl sm:text-4xl text-purple-600" />
                </div>
              </div>
            </div>

            <div v-if="selectedModulo" class="p-6 sm:p-8 pt-12 sm:pt-14">
              <div class="mb-6">
                <h3 class="text-xl sm:text-2xl font-black text-gray-900 dark:text-white leading-tight mb-1">{{ selectedModulo.nombre }}</h3>
                <p class="text-xs font-black text-purple-500 uppercase tracking-[0.2em] break-all">{{ selectedModulo.ruta || '/' }}</p>
              </div>

              <div class="space-y-5">
                <div class="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-700">
                  <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Descripción General</p>
                  <p class="text-sm font-medium text-gray-600 dark:text-gray-300 italic">
                    {{ selectedModulo.descripcion || 'Sin descripción registrada.' }}
                  </p>
                </div>

                <div class="grid grid-cols-2 gap-4">
                  <div class="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-700">
                    <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Identificador</p>
                    <p class="text-sm font-black text-gray-900 dark:text-white">#{{ selectedModulo.idModulo }}</p>
                  </div>
                  <div class="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-700">
                    <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Nivel</p>
                    <p class="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tighter">
                      {{ getNivel(selectedModulo) === 0 ? 'Raíz' : 'Nivel ' + getNivel(selectedModulo) }}
                    </p>
                  </div>
                </div>

                <div class="grid grid-cols-2 gap-4">
                  <div class="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-700">
                    <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Orden</p>
                    <p class="text-sm font-black text-gray-900 dark:text-white">#{{ selectedModulo.orden ?? 0 }}</p>
                  </div>
                  <div class="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-700">
                    <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Tipo</p>
                    <p class="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tighter">
                      {{ selectedModulo.tipo || 'modulo' }}
                    </p>
                  </div>
                </div>

                <div class="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-700">
                  <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Estado</p>
                  <div class="flex items-center gap-1.5">
                    <div :class="['w-2 h-2 rounded-full animate-pulse', selectedModulo.estado?.toLowerCase() === 'activo' ? 'bg-emerald-500' : 'bg-red-500']"></div>
                    <p class="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tighter">{{ selectedModulo.estado }}</p>
                  </div>
                </div>

                <div v-if="selectedModulo.idPadre" class="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-100 dark:border-blue-800">
                  <p class="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-1">Pertenece a</p>
                  <p class="text-sm font-black text-blue-600 dark:text-blue-300">{{ getNombrePadre(selectedModulo.idPadre) }}</p>
                </div>

                <div v-if="getHijosCount(selectedModulo.idModulo) > 0" class="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-2xl border border-indigo-100 dark:border-indigo-800">
                  <p class="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1">Contiene</p>
                  <p class="text-sm font-black text-indigo-600 dark:text-indigo-300">
                    {{ getHijosCount(selectedModulo.idModulo) }} submódulo(s)
                  </p>
                </div>

                <div class="pt-6 flex gap-3">
                  <button
                    @click="openEditModal(selectedModulo); closeDetailModal()"
                    class="flex-1 px-6 py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-2xl font-black text-xs uppercase tracking-widest hover:scale-[1.02] transition-all shadow-lg"
                  >
                    <Icon icon="mdi:pencil" class="inline mr-2" />
                    Editar Datos
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

        <!-- Modal Organizador Visual del Menú (Muñeca Rusa) -->
    <Teleport to="body">
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="opacity-0 scale-95" enter-to-class="opacity-100 scale-100" leave-active-class="transition duration-200 ease-in" leave-from-class="opacity-100 scale-100" leave-to-class="opacity-0 scale-95">
        <div v-if="showOrganizarModal" class="fixed inset-0 bg-black/70 flex items-center justify-center p-3 sm:p-5 z-[95] backdrop-blur-[2px] overflow-y-auto" @click.self="closeOrganizarModal">
          <div class="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-4xl mx-auto my-auto overflow-hidden flex flex-col max-h-[92vh] border border-gray-100 dark:border-gray-700">
            <!-- Header -->
            <div class="p-6 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white flex justify-between items-center relative overflow-hidden">
              <div class="flex items-center gap-3">
                <div class="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center text-white shadow-inner">
                  <Icon icon="mdi:sort-variant" class="text-2xl" />
                </div>
                <div>
                  <h2 class="text-xl font-black tracking-tight">Organizador del Menú (Muñeca Rusa)</h2>
                  <p class="text-xs text-indigo-100 font-medium">Configura qué módulo va primero en la barra superior y el orden de los submódulos</p>
                </div>
              </div>
              <button @click="closeOrganizarModal" class="p-2 hover:bg-white/20 rounded-xl transition-colors">
                <Icon icon="mdi:close" class="text-xl" />
              </button>
            </div>

            <!-- Pestañas de Organización -->
            <div class="flex border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 px-6 pt-3 gap-3">
              <button
                @click="tabOrganizar = 'raices'"
                class="pb-3 px-4 font-black text-xs uppercase tracking-wider flex items-center gap-2 border-b-2 transition-all"
                :class="tabOrganizar === 'raices' ? 'border-purple-600 text-purple-600 dark:text-purple-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'"
              >
                <Icon icon="mdi:view-column" class="text-base" />
                1. Módulos de Barra Principal ({{ modulosRaizOrganizar.length }})
              </button>
              <button
                @click="tabOrganizar = 'submodulos'"
                class="pb-3 px-4 font-black text-xs uppercase tracking-wider flex items-center gap-2 border-b-2 transition-all"
                :class="tabOrganizar === 'submodulos' ? 'border-purple-600 text-purple-600 dark:text-purple-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'"
              >
                <Icon icon="mdi:file-tree" class="text-base" />
                2. Submódulos por Grupo
              </button>
            </div>

            <!-- Contenido Modal -->
            <div class="p-6 overflow-y-auto flex-1 space-y-4">
              <!-- PESTAÑA 1: MÓDULOS RAÍZ -->
              <div v-if="tabOrganizar === 'raices'">
                <div class="mb-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl p-3 flex items-start gap-3">
                  <Icon icon="mdi:information-outline" class="text-indigo-600 dark:text-indigo-400 text-lg flex-shrink-0 mt-0.5" />
                  <p class="text-xs text-indigo-900 dark:text-indigo-200">
                    Aquí decides <strong>qué módulo va de primero, segundo, tercero, etc.</strong> de izquierda a derecha en la barra superior.
                    Usa las flechas <strong>▲ Subir</strong> y <strong>▼ Bajar</strong> o escribe directamente el número de orden.
                  </p>
                </div>

                <div class="space-y-2">
                  <div
                    v-for="(mod, idx) in modulosRaizOrganizar"
                    :key="mod.idModulo"
                    class="p-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all flex items-center justify-between gap-4"
                  >
                    <!-- Indicador de orden y posición -->
                    <div class="flex items-center gap-3 min-w-0">
                      <div class="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center font-black text-sm flex-shrink-0 border border-purple-200 dark:border-purple-800">
                        #{{ mod.orden ?? (idx + 1) }}
                      </div>
                      <div class="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-200 flex-shrink-0">
                        <Icon :icon="mod.icono || 'mdi:folder'" class="text-xl" />
                      </div>
                      <div class="min-w-0">
                        <h4 class="font-black text-sm text-gray-900 dark:text-white truncate">{{ mod.nombre }}</h4>
                        <div class="flex items-center gap-2 mt-0.5">
                          <span v-if="contarHijos(mod.idModulo) > 0" class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 inline-flex items-center gap-1">
                            <Icon icon="mdi:folder-multiple" class="text-xs" />
                            {{ contarHijos(mod.idModulo) }} submódulos
                          </span>
                          <span v-else class="text-[10px] text-gray-400 italic">Sin submódulos</span>
                        </div>
                      </div>
                    </div>

                    <!-- Controles de orden -->
                    <div class="flex items-center gap-2 flex-shrink-0">
                      <button
                        v-if="contarHijos(mod.idModulo) > 0"
                        @click="seleccionarPadreParaOrganizar(mod.idModulo)"
                        class="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-600 hover:text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1"
                        title="Ver y ordenar submódulos internos"
                      >
                        <Icon icon="mdi:folder-open" class="text-sm" />
                        Ver Submódulos
                      </button>

                      <div class="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-xl">
                        <button
                          @click="moverEnListaRaices(idx, -1)"
                          :disabled="idx === 0"
                          class="w-8 h-8 rounded-lg flex items-center justify-center bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-purple-600 hover:text-white transition-all disabled:opacity-30 disabled:pointer-events-none shadow-sm"
                          title="Mover hacia arriba"
                        >
                          <Icon icon="mdi:arrow-up-bold" class="text-base" />
                        </button>
                        <input
                          v-model.number="mod.orden"
                          type="number"
                          class="w-12 h-8 text-center text-xs font-black bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                          min="1"
                        />
                        <button
                          @click="moverEnListaRaices(idx, 1)"
                          :disabled="idx === modulosRaizOrganizar.length - 1"
                          class="w-8 h-8 rounded-lg flex items-center justify-center bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-purple-600 hover:text-white transition-all disabled:opacity-30 disabled:pointer-events-none shadow-sm"
                          title="Mover hacia abajo"
                        >
                          <Icon icon="mdi:arrow-down-bold" class="text-base" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- PESTAÑA 2: SUBMÓDULOS POR GRUPO -->
              <div v-else-if="tabOrganizar === 'submodulos'">
                <div class="mb-4">
                  <label class="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2">
                    Selecciona el Módulo Padre para ordenar lo que tiene adentro:
                  </label>
                  <select
                    v-model="padreSeleccionadoId"
                    class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl font-bold text-sm text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-purple-500 cursor-pointer"
                  >
                    <option
                      v-for="p in modulosPadreDisponibles"
                      :key="p.idModulo"
                      :value="p.idModulo"
                    >
                      {{ '- '.repeat(getNivel(p)) }}{{ p.nombre }} ({{ contarHijos(p.idModulo) }} submódulos)
                    </option>
                  </select>
                </div>

                <div v-if="submodulosDelPadre.length === 0" class="text-center py-10 bg-gray-50 dark:bg-gray-900/40 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
                  <Icon icon="mdi:folder-open-outline" class="text-4xl text-gray-400 mx-auto mb-2" />
                  <p class="text-sm font-bold text-gray-500">Este módulo no tiene submódulos asignados</p>
                </div>

                <div v-else class="space-y-2">
                  <div
                    v-for="(sub, sIdx) in submodulosDelPadre"
                    :key="sub.idModulo"
                    class="p-4 bg-white dark:bg-gray-800 rounded-2xl border shadow-sm transition-all flex items-center justify-between gap-4"
                    :class="contarHijos(sub.idModulo) > 0 ? 'border-indigo-300 dark:border-indigo-700 bg-indigo-50/20' : 'border-gray-200 dark:border-gray-700'"
                  >
                    <div class="flex items-center gap-3 min-w-0">
                      <div class="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-black text-sm flex-shrink-0 border border-indigo-200 dark:border-indigo-800">
                        #{{ sub.orden ?? (sIdx + 1) }}
                      </div>
                      <div class="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-200 flex-shrink-0">
                        <Icon :icon="sub.icono || 'mdi:folder'" class="text-xl" />
                      </div>
                      <div class="min-w-0">
                        <div class="flex items-center gap-2">
                          <h4 class="font-black text-sm text-gray-900 dark:text-white truncate">{{ sub.nombre }}</h4>
                          <!-- Si es contenedor (muñeca rusa) -->
                          <span v-if="contarHijos(sub.idModulo) > 0" class="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800 inline-flex items-center gap-1">
                            <Icon icon="mdi:folder-table" class="text-xs" />
                            Contiene {{ contarHijos(sub.idModulo) }} sub-módulos
                          </span>
                        </div>
                        <p class="text-xs text-gray-400 font-mono mt-0.5 truncate">{{ sub.ruta || 'Sin ruta' }}</p>
                      </div>
                    </div>

                    <!-- Controles -->
                    <div class="flex items-center gap-2 flex-shrink-0">
                      <button
                        v-if="contarHijos(sub.idModulo) > 0"
                        @click="padreSeleccionadoId = sub.idModulo"
                        class="px-3 py-1.5 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 hover:bg-purple-600 hover:text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1"
                        title="Abrir este nivel de la muñeca rusa"
                      >
                        <Icon icon="mdi:subdirectory-arrow-right" class="text-sm" />
                        Ordenar sus hijos
                      </button>

                      <div class="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-xl">
                        <button
                          @click="moverEnListaSubmodulos(sIdx, -1)"
                          :disabled="sIdx === 0"
                          class="w-8 h-8 rounded-lg flex items-center justify-center bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-indigo-600 hover:text-white transition-all disabled:opacity-30 disabled:pointer-events-none shadow-sm"
                          title="Mover hacia arriba"
                        >
                          <Icon icon="mdi:arrow-up-bold" class="text-base" />
                        </button>
                        <input
                          v-model.number="sub.orden"
                          type="number"
                          class="w-12 h-8 text-center text-xs font-black bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                          min="1"
                        />
                        <button
                          @click="moverEnListaSubmodulos(sIdx, 1)"
                          :disabled="sIdx === submodulosDelPadre.length - 1"
                          class="w-8 h-8 rounded-lg flex items-center justify-center bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-indigo-600 hover:text-white transition-all disabled:opacity-30 disabled:pointer-events-none shadow-sm"
                          title="Mover hacia abajo"
                        >
                          <Icon icon="mdi:arrow-down-bold" class="text-base" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Footer con Guardar -->
            <div class="p-4 bg-gray-50 dark:bg-gray-900/60 border-t border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-center gap-3">
              <span class="text-xs text-gray-500 dark:text-gray-400 font-medium ml-2">
                Los cambios se reflejarán inmediatamente en el menú de navegación.
              </span>
              <div class="flex items-center gap-3">
                <button
                  @click="closeOrganizarModal"
                  class="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-bold text-xs hover:bg-gray-100 dark:hover:bg-gray-700 transition-all"
                >
                  Cancelar
                </button>
                <button
                  @click="guardarOrdenCompleto"
                  :disabled="guardandoOrden"
                  class="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-black text-xs hover:shadow-lg hover:shadow-indigo-200 dark:hover:shadow-none transition-all flex items-center gap-2 disabled:opacity-50"
                >
                  <Icon v-if="guardandoOrden" icon="mdi:loading" class="animate-spin text-base" />
                  <Icon v-else icon="mdi:content-save-check" class="text-base" />
                  {{ guardandoOrden ? 'GUARDANDO CAMBIOS...' : 'GUARDAR NUEVO ORDEN' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

<!-- Modal de confirmación - RESPONSIVE -->
    <Teleport to="body">
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="opacity-0 scale-95" enter-to-class="opacity-100 scale-100" leave-active-class="transition duration-200 ease-in" leave-from-class="opacity-100 scale-100" leave-to-class="opacity-0 scale-95">
        <div
          v-if="showConfirmModal"
          class="fixed inset-0 bg-black/70 flex items-center justify-center p-2 sm:p-4 z-[100] backdrop-blur-[2px] overflow-y-auto"
          @click.self="cancelConfirm"
        >
          <div class="bg-white dark:bg-gray-800 rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-md mx-auto my-auto overflow-hidden transform transition-all">
            <div :class="[
              'px-4 sm:px-6 py-4 sm:py-6 text-center border-b border-gray-100 dark:border-gray-700',
              confirmConfig.type === 'danger' ? 'bg-red-50 dark:bg-red-900/20' :
              confirmConfig.type === 'warning' ? 'bg-amber-50 dark:bg-amber-900/20' :
              'bg-blue-50 dark:bg-blue-900/20'
            ]">
              <div :class="[
                'w-14 h-14 sm:w-16 sm:h-16 mx-auto rounded-full flex items-center justify-center mb-3 sm:mb-4 shadow-sm',
                confirmConfig.type === 'danger' ? 'bg-red-100 dark:bg-red-800 text-red-600 dark:text-red-300' :
                confirmConfig.type === 'warning' ? 'bg-amber-100 dark:bg-amber-800 text-amber-600 dark:text-amber-300' :
                'bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-300'
              ]">
                <Icon
                  :icon="confirmConfig.type === 'danger' ? 'mdi:alert-octagon' :
                        confirmConfig.type === 'warning' ? 'mdi:alert' :
                        'mdi:information'"
                  class="text-2xl sm:text-3xl"
                />
              </div>
              <h3 class="text-lg sm:text-xl font-black text-gray-900 dark:text-white lowercase first-letter:uppercase tracking-tight">{{ confirmConfig.title }}</h3>
            </div>

            <div class="p-6 sm:p-8 text-center">
              <p class="text-gray-600 dark:text-gray-400 font-medium mb-1 text-sm sm:text-base" v-html="confirmConfig.message"></p>
              <p v-if="confirmConfig.detail" class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-2">{{ confirmConfig.detail }}</p>
            </div>

            <div class="px-4 sm:px-8 pb-6 sm:pb-8 flex flex-col sm:flex-row gap-3">
              <button
                @click="cancelConfirm"
                class="flex-1 px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-2xl text-gray-700 dark:text-gray-300 font-black text-xs uppercase tracking-widest hover:bg-gray-50 transition-all"
              >
                {{ confirmConfig.cancelText }}
              </button>
              <button
                @click="executeConfirm"
                :class="[
                  'flex-1 px-4 py-3 rounded-2xl text-white font-black text-xs uppercase tracking-widest transition-all shadow-lg',
                  confirmConfig.type === 'danger' ? 'bg-red-600 hover:bg-red-700 shadow-red-200 dark:shadow-none' :
                  confirmConfig.type === 'warning' ? 'bg-amber-600 hover:bg-amber-700 shadow-amber-200 dark:shadow-none' :
                  'bg-blue-600 hover:bg-blue-700 shadow-blue-200 dark:shadow-none'
                ]"
              >
                {{ confirmConfig.confirmText }}
              </button>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { debounce } from 'lodash';
import { useToast } from "vue-toastification";

import {
  obtenerModulos,
  crearModulo,
  actualizarModulo,
  eliminarModulo,
  cambiarEstadoModulo,
  verificarNombreModulo,
  buscarModulos,
  filtrarModulosPorEstado,
  obtenerEstadisticas,
  ordenarModulos,
  verificarModuloTienePermisos,
  obtenerIconosDisponibles,
  formatearFechaModulo
} from '@/api/modulo.js';

import {
  imprimirReporteModulos,
  imprimirReporteSimplificado
} from '@/utils/reporteModulosGenerator.js';

export default {
  name: 'ModulosList',
  components: { Icon },
  data() {
    return {
      modulos: [],
      loading: false,
      saving: false,
      showModal: false,
      showDetailModal: false,
      showExportMenu: false,
      showConfirmModal: false,
      showIconPicker: false,
      iconSearch: '',
      iconosDisponibles: [
        // GENERAL
        'mdi:folder-outline', 'mdi:folder', 'mdi:folder-multiple', 'mdi:file-tree',
        'mdi:home', 'mdi:home-outline', 'mdi:view-dashboard', 'mdi:view-grid',
        'mdi:apps', 'mdi:application', 'mdi:application-outline', 'mdi:application-settings',
        'mdi:cog', 'mdi:cog-outline', 'mdi:cogs', 'mdi:wrench', 'mdi:wrench-outline',
        'mdi:tools', 'mdi:hammer', 'mdi:hammer-wrench', 'mdi:toolbox',
        // COMERCIO
        'mdi:cart', 'mdi:cart-outline', 'mdi:cart-plus', 'mdi:cart-minus',
        'mdi:store', 'mdi:store-outline', 'mdi:storefront', 'mdi:storefront-outline',
        'mdi:shopping', 'mdi:shopping-outline', 'mdi:tag', 'mdi:tag-multiple-outline',
        'mdi:barcode', 'mdi:barcode-scan', 'mdi:qrcode-scan', 'mdi:package-variant',
        'mdi:package-variant-closed', 'mdi:package', 'mdi:inventory',
        'mdi:cash', 'mdi:cash-multiple', 'mdi:cash-register', 'mdi:cash-check',
        'mdi:cash-minus', 'mdi:cash-plus', 'mdi:cash-clock',
        'mdi:credit-card', 'mdi:credit-card-outline', 'mdi:credit-card-scan',
        'mdi:bank', 'mdi:bank-outline', 'mdi:wallet', 'mdi:wallet-outline',
        'mdi:receipt', 'mdi:receipt-text-outline', 'mdi:file-invoice',
        'mdi:file-invoice-outline',
        // USUARIOS
        'mdi:account', 'mdi:account-outline', 'mdi:account-group',
        'mdi:account-group-outline', 'mdi:account-multiple', 'mdi:account-multiple-outline',
        'mdi:account-cash', 'mdi:account-cash-outline', 'mdi:account-wrench',
        'mdi:account-wrench-outline', 'mdi:account-tie', 'mdi:account-tie-outline',
        'mdi:account-shield', 'mdi:account-shield-outline', 'mdi:account-lock',
        'mdi:account-lock-outline', 'mdi:account-circle', 'mdi:account-circle-outline',
        'mdi:account-key', 'mdi:account-key-outline', 'mdi:shield-account',
        'mdi:shield-account-outline', 'mdi:card-account-details',
        // SERVICIOS
        'mdi:lightning-bolt', 'mdi:flash', 'mdi:flash-outline',
        'mdi:clock-outline', 'mdi:clock-check-outline', 'mdi:clipboard-list',
        'mdi:clipboard-list-outline', 'mdi:clipboard-check', 'mdi:clipboard-text',
        'mdi:clipboard-text-outline', 'mdi:clipboard-account',
        'mdi:format-list-bulleted', 'mdi:format-list-checks',
        'mdi:router-wireless', 'mdi:router', 'mdi:wifi', 'mdi:network',
        'mdi:moped-electric', 'mdi:moped', 'mdi:truck-delivery', 'mdi:truck',
        'mdi:truck-outline', 'mdi:truck-check', 'mdi:car', 'mdi:car-outline',
        // DOCUMENTOS
        'mdi:file-document', 'mdi:file-document-outline', 'mdi:file-document-check',
        'mdi:file-document-check-outline', 'mdi:file-multiple', 'mdi:file-cabinet',
        'mdi:folder-open', 'mdi:folder-account', 'mdi:notebook', 'mdi:notebook-outline',
        'mdi:book-open', 'mdi:book-open-outline', 'mdi:book-open-page-variant',
        'mdi:note-text', 'mdi:note-text-outline', 'mdi:text-box', 'mdi:text-box-outline',
        // ANALÍTICA
        'mdi:chart-box', 'mdi:chart-box-outline', 'mdi:chart-line', 'mdi:chart-line-variant',
        'mdi:chart-bar', 'mdi:chart-bar-stacked', 'mdi:chart-pie', 'mdi:chart-donut',
        'mdi:chart-scatter-plot', 'mdi:chart-areaspline', 'mdi:chart-timeline-variant',
        'mdi:analytics', 'mdi:google-analytics', 'mdi:trending-up', 'mdi:trending-down',
        'mdi:presentation', 'mdi:presentation-play', 'mdi:monitor-dashboard',
        'mdi:view-dashboard-outline', 'mdi:view-dashboard-variant',
        // SEGURIDAD
        'mdi:shield', 'mdi:shield-outline', 'mdi:shield-check', 'mdi:shield-check-outline',
        'mdi:shield-key', 'mdi:shield-key-outline', 'mdi:shield-lock', 'mdi:shield-lock-outline',
        'mdi:key', 'mdi:key-outline', 'mdi:key-variant', 'mdi:key-chain',
        'mdi:lock', 'mdi:lock-outline', 'mdi:lock-open', 'mdi:lock-open-check',
        'mdi:lock-open-outline', 'mdi:lock-check', 'mdi:lock-alert',
        'mdi:security', 'mdi:security-network', 'mdi:eye', 'mdi:eye-outline',
        'mdi:eye-off', 'mdi:eye-off-outline', 'mdi:fingerprint',
        // HERRAMIENTAS
        'mdi:swap-horizontal', 'mdi:swap-vertical', 'mdi:swap-vertical-variant',
        'mdi:swap-horizontal-variant', 'mdi:keyboard-return', 'mdi:keyboard-return-outline',
        'mdi:refresh', 'mdi:reload', 'mdi:sync', 'mdi:auto-fix',
        'mdi:plus-circle', 'mdi:plus-circle-outline', 'mdi:minus-circle',
        'mdi:minus-circle-outline', 'mdi:pencil', 'mdi:pencil-outline',
        'mdi:delete', 'mdi:delete-outline', 'mdi:content-save',
        'mdi:content-save-outline', 'mdi:printer', 'mdi:printer-outline',
        'mdi:download', 'mdi:upload', 'mdi:import', 'mdi:export',
        'mdi:share', 'mdi:share-variant', 'mdi:link', 'mdi:link-variant',
        // COMUNICACIÓN
        'mdi:email', 'mdi:email-outline', 'mdi:email-multiple', 'mdi:message',
        'mdi:message-outline', 'mdi:whatsapp', 'mdi:phone', 'mdi:phone-outline',
        'mdi:bell', 'mdi:bell-outline', 'mdi:bell-ring', 'mdi:bell-ring-outline',
        'mdi:calendar', 'mdi:calendar-outline', 'mdi:calendar-check',
        'mdi:calendar-clock', 'mdi:calendar-month', 'mdi:calendar-today',
        'mdi:clock', 'mdi:clock-outline', 'mdi:timer', 'mdi:timer-outline',
        'mdi:update', 'mdi:history',
        // UBICACIÓN
        'mdi:map-marker', 'mdi:map-marker-outline', 'mdi:store-marker',
        'mdi:store-marker-outline', 'mdi:office-building', 'mdi:office-building-outline',
        'mdi:domain', 'mdi:factory', 'mdi:home-city', 'mdi:home-city-outline',
        'mdi:city', 'mdi:city-variant-outline',
        // RESTAURANTE
        'mdi:silverware', 'mdi:silverware-fork-knife', 'mdi:silverware-fork',
        'mdi:silverware-spoon', 'mdi:food', 'mdi:food-outline', 'mdi:food-fork-drink',
        'mdi:hamburger', 'mdi:pizza', 'mdi:coffee', 'mdi:coffee-outline',
        'mdi:glass-wine', 'mdi:glass-cocktail', 'mdi:beer', 'mdi:beer-outline',
        'mdi:cake-variant', 'mdi:cake', 'mdi:chef-hat', 'mdi:noodles',
        // SALUD
        'mdi:hospital', 'mdi:hospital-building', 'mdi:medical-bag',
        'mdi:medical-bag-outline', 'mdi:heart-pulse', 'mdi:needle',
        'mdi:pill', 'mdi:pill-multiple', 'mdi:stethoscope',
        // EDUCACIÓN
        'mdi:school', 'mdi:school-outline', 'mdi:book-education',
        'mdi:book-education-outline', 'mdi:certificate', 'mdi:certificate-outline',
        'mdi:graduation-cap', 'mdi:graduation-cap-outline',
        // VARIOS
        'mdi:star', 'mdi:star-outline', 'mdi:heart', 'mdi:heart-outline',
        'mdi:thumb-up', 'mdi:thumb-down', 'mdi:check-circle', 'mdi:check-circle-outline',
        'mdi:alert-circle', 'mdi:alert-circle-outline', 'mdi:information',
        'mdi:information-outline', 'mdi:help-circle', 'mdi:help-circle-outline',
        'mdi:lightbulb', 'mdi:lightbulb-outline', 'mdi:palette', 'mdi:palette-outline',
        'mdi:image', 'mdi:image-outline', 'mdi:camera', 'mdi:camera-outline',
        'mdi:video', 'mdi:video-outline', 'mdi:music', 'mdi:music-note',
        'mdi:gamepad-variant', 'mdi:gamepad-variant-outline', 'mdi:puzzle',
        'mdi:puzzle-outline', 'mdi:brain', 'mdi:robot', 'mdi:robot-outline',
        'mdi:atom', 'mdi:flask', 'mdi:flask-outline', 'mdi:beaker',
        'mdi:beaker-outline', 'mdi:test-tube', 'mdi:microscope',
        'mdi:leaf', 'mdi:leaf-circle', 'mdi:tree', 'mdi:tree-outline',
        'mdi:flower', 'mdi:flower-outline', 'mdi:sprout', 'mdi:sprout-outline'
      ],
      confirmConfig: {
        title: 'Confirmar',
        message: '',
        detail: '',
        confirmText: 'Aceptar',
        cancelText: 'Cancelar',
        type: 'info',
        onConfirm: null,
        onCancel: null
      },
      searchQuery: '',
      filterStatus: 'all',
      filterTipo: 'all',
      sortBy: 'orden',
      currentPage: 1,
      itemsPerPage: 10,
      form: {
        idModulo: null,
        idPadre: null,
        nombre: '',
        descripcion: '',
        icono: 'mdi:folder-outline',
        ruta: '',
        estado: 'Activo',
        orden: 0
      },
      errors: {},
      editingModulo: null,
      selectedModulo: null,
      lastUpdate: new Date(),
      showOrganizarModal: false,
      tabOrganizar: 'raices',
      padreSeleccionadoId: null,
      guardandoOrden: false,
      modulosRaizOrganizar: [],
      submodulosDelPadre: [],
    };
  },
  computed: {
    filteredModulos() {
      let filtered = [...this.modulos];

      if (this.searchQuery.trim()) {
        filtered = buscarModulos(filtered, this.searchQuery);
      }

      filtered = filtrarModulosPorEstado(filtered, this.filterStatus);

      // Filtro por nivel (raíz / hijos) y por tipo (modulo / submodulo)
      if (this.filterTipo === 'raiz') {
        filtered = filtered.filter(m => !m.idPadre);
      } else if (this.filterTipo === 'conPadre') {
        filtered = filtered.filter(m => m.idPadre);
      } else if (this.filterTipo === 'tipo_modulo') {
        filtered = filtered.filter(m => (m.tipo || 'modulo') === 'modulo');
      } else if (this.filterTipo === 'tipo_submodulo') {
        filtered = filtered.filter(m => m.tipo === 'submodulo');
      }

      // Ordenar: primero por orden de padre (nivel), luego por criterio
      filtered = this.ordenarConJerarquia(filtered);

      return filtered;
    },

    filterTipoLabel() {
      const labels = {
        raiz: 'Raíces',
        conPadre: 'Hijos',
        tipo_modulo: 'Tipo módulo',
        tipo_submodulo: 'Tipo submódulo'
      };
      return labels[this.filterTipo] || this.filterTipo;
    },

    estadisticas() {
      return obtenerEstadisticas(this.modulos);
    },

    modulosPadreDisponibles() {
      return this.modulos
        .filter(m => {
          // No puede ser su propio padre
          if (m.idModulo === this.form.idModulo) return false;
          // Si estamos editando, excluir descendientes para no crear ciclos
          if (this.form.idModulo && this.esDescendiente(m.idModulo, this.form.idModulo)) {
            return false;
          }
          return true;
        })
        .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
    },

    filteredIcons() {
      if (!this.iconSearch.trim()) return this.iconosDisponibles;
      const search = this.iconSearch.toLowerCase().replace('mdi:', '').trim();
      return this.iconosDisponibles.filter(icon =>
        icon.toLowerCase().includes(search)
      );
    },

    paginatedModulos() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredModulos.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredModulos.length / this.itemsPerPage) || 1;
    },

    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },

    startItem() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },

    endItem() {
      return Math.min(this.currentPage * this.itemsPerPage, this.filteredModulos.length);
    },

    isFormValid() {
      return this.form.nombre.trim() && this.form.ruta.trim();
    },

    isFiltered() {
      return this.searchQuery.trim() || this.filterStatus !== 'all' || this.filterTipo !== 'all';
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  mounted() {
    this.loadModulos();
  },
  watch: {
    padreSeleccionadoId() {
      this.cargarSubmodulosDelPadre();
    },
    filterStatus() { this.currentPage = 1; },
    filterTipo()   { this.currentPage = 1; },
    searchQuery()  { this.currentPage = 1; },
    sortBy()       { this.currentPage = 1; },
  },
  methods: {
    // =====================================================
    // JERARQUÍA — Auxiliares
    // =====================================================
    getNivel(modulo) {
      if (!modulo) return 0;
      let nivel = 0;
      let actual = modulo;
      const visitados = new Set();

      while (actual?.idPadre && !visitados.has(actual.idModulo)) {
        visitados.add(actual.idModulo);
        nivel++;
        actual = this.modulos.find(m => m.idModulo === actual.idPadre);
      }
      return nivel;
    },

    esDescendiente(posibleHijoId, ancestroId) {
      let actual = this.modulos.find(m => m.idModulo === posibleHijoId);
      const visitados = new Set();

      while (actual?.idPadre && !visitados.has(actual.idModulo)) {
        visitados.add(actual.idModulo);
        if (actual.idPadre === ancestroId) return true;
        actual = this.modulos.find(m => m.idModulo === actual.idPadre);
      }
      return false;
    },

    getHijosCount(idModulo) {
      return this.modulos.filter(m => m.idPadre === idModulo).length;
    },

    /**
     * Ordena el listado respetando la jerarquía: cada padre seguido de sus hijos.
     */
    ordenarConJerarquia(lista) {
      if (!lista || lista.length === 0) return lista;

      // Índice por id
      const byId = new Map(lista.map(m => [m.idModulo, m]));

      // Comparador según sortBy
      const cmp = (a, b) => {
        switch (this.sortBy) {
          case 'nombre':
            return (a.nombre || '').toLowerCase().localeCompare((b.nombre || '').toLowerCase());
          case 'nombre_desc':
            return (b.nombre || '').toLowerCase().localeCompare((a.nombre || '').toLowerCase());
          case 'ruta':
            return (a.ruta || '').localeCompare(b.ruta || '');
          case 'fechaCreacion_desc':
            return new Date(b.fechaCreacion || 0) - new Date(a.fechaCreacion || 0);
          case 'orden':
          default:
            return (a.orden ?? 0) - (b.orden ?? 0);
        }
      };

      // Hijos por padre (solo los que están en la lista filtrada)
      const hijosPorPadre = new Map();
      const raices = [];
      for (const m of lista) {
        if (!m.idPadre || !byId.has(m.idPadre)) {
          raices.push(m);
        } else {
          if (!hijosPorPadre.has(m.idPadre)) hijosPorPadre.set(m.idPadre, []);
          hijosPorPadre.get(m.idPadre).push(m);
        }
      }

      raices.sort(cmp);
      for (const hijos of hijosPorPadre.values()) hijos.sort(cmp);

      // Recorrido DFS preorden
      const resultado = [];
      const visitados = new Set();

      const dfs = (nodo, nivel) => {
        if (!nodo || visitados.has(nodo.idModulo)) return;
        visitados.add(nodo.idModulo);
        resultado.push({ ...nodo, _nivelTemp: nivel });
        const hijos = hijosPorPadre.get(nodo.idModulo) || [];
        for (const h of hijos) dfs(h, nivel + 1);
      };

      for (const r of raices) dfs(r, 0);

      // Si algún módulo quedó sin visitar (por ciclos), añadirlo al final
      for (const m of lista) {
        if (!visitados.has(m.idModulo)) resultado.push(m);
      }

      return resultado;
    },

    // =====================================================
    // CONFIRMACIÓN
    // =====================================================
    showConfirm(options) {
      return new Promise((resolve) => {
        this.confirmConfig = {
          title: options.title || 'Confirmar',
          message: options.message || '¿Estás seguro?',
          detail: options.detail || '',
          confirmText: options.confirmText || 'Aceptar',
          cancelText: options.cancelText || 'Cancelar',
          type: options.type || 'info',
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false)
        };
        this.showConfirmModal = true;
      });
    },

    cancelConfirm() {
      this.showConfirmModal = false;
      if (this.confirmConfig.onCancel) this.confirmConfig.onCancel();
    },

    executeConfirm() {
      this.showConfirmModal = false;
      if (this.confirmConfig.onConfirm) this.confirmConfig.onConfirm();
    },

    selectIcon(icon) {
      this.form.icono = icon;
      this.showIconPicker = false;
      this.iconSearch = '';
    },

    closeIconPicker() {
      this.showIconPicker = false;
      this.iconSearch = '';
    },

    async loadModulos() {
      this.loading = true;
      try {
        let estado = this.filterStatus;
        if (estado === 'activos') estado = 'activos';
        else if (estado === 'inactivos') estado = 'inactivos';
        else estado = 'all';

        const response = await obtenerModulos(estado);

        if (response.success) {
          this.modulos = response.data || [];
          this.lastUpdate = new Date();
          console.log('✅ Módulos cargados:', this.modulos.length);
        } else {
          throw new Error(response.error || 'Error al cargar módulos');
        }
      } catch (error) {
        console.error('❌ Error al cargar módulos:', error);
        this.toast.error('Error al cargar los módulos: ' + error.message);
      } finally {
        this.loading = false;
      }
    },

    handleFilterChange() {
      this.currentPage = 1;
      this.loadModulos();
    },

    handleSearch: debounce(function() {
      this.currentPage = 1;
    }, 300),

    getNombrePadre(idPadre) {
      if (!idPadre) return '—';
      const padre = this.modulos.find(m => m.idModulo === idPadre);
      return padre ? padre.nombre : `ID ${idPadre}`;
    },

    openCreateModal() {
      this.editingModulo = null;
      this.resetForm();
      this.errors = {};
      this.showIconPicker = false;
      this.iconSearch = '';
      this.showModal = true;
    },

    openEditModal(modulo) {
      this.editingModulo = modulo;
      this.form = {
        idModulo: modulo.idModulo,
        idPadre: modulo.idPadre ? Number(modulo.idPadre) : null,
        nombre: modulo.nombre,
        descripcion: modulo.descripcion || '',
        icono: modulo.icono || 'mdi:folder-outline',
        ruta: modulo.ruta || '',
        estado: modulo.estado || 'Activo',
        orden: modulo.orden ?? 0
      };
      this.errors = {};
      this.showIconPicker = false;
      this.iconSearch = '';
      this.showModal = true;
    },

    openDetailModal(modulo) {
      this.selectedModulo = modulo;
      this.showDetailModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.showIconPicker = false;
      this.iconSearch = '';
      this.resetForm();
      this.errors = {};
    },

    closeDetailModal() {
      this.showDetailModal = false;
      this.selectedModulo = null;
    },

    resetForm() {
      this.form = {
        idModulo: null,
        idPadre: null,
        nombre: '',
        descripcion: '',
        icono: 'mdi:folder-outline',
        ruta: '',
        estado: 'Activo',
        orden: 0
      };
      this.editingModulo = null;
    },

    validateForm() {
      this.errors = {};

      if (!this.form.nombre.trim()) {
        this.errors.nombre = 'El nombre es requerido';
      } else if (this.form.nombre.length > 100) {
        this.errors.nombre = 'El nombre no puede exceder 100 caracteres';
      }

      if (!this.form.ruta.trim()) {
        this.errors.ruta = 'La ruta es requerida';
      } else if (this.form.ruta.length > 200) {
        this.errors.ruta = 'La ruta no puede exceder 200 caracteres';
      } else if (!this.form.ruta.startsWith('/')) {
        this.errors.ruta = 'La ruta debe comenzar con "/"';
      }

      if (this.form.descripcion && this.form.descripcion.length > 500) {
        this.errors.descripcion = 'La descripción no puede exceder 500 caracteres';
      }

      return Object.keys(this.errors).length === 0;
    },

    async saveModulo() {
      if (!this.validateForm()) return;

      this.saving = true;
      try {
        if (!this.editingModulo) {
          const verifyResult = await verificarNombreModulo(this.form.nombre);
          if (verifyResult.success && verifyResult.existe) {
            this.errors.nombre = 'Este nombre de módulo ya está registrado';
            this.saving = false;
            return;
          }
        }

        const tipoFinal = this.form.idPadre ? 'submodulo' : 'modulo';

        const moduloData = {
          idPadre: this.form.idPadre ? Number(this.form.idPadre) : null,
          nombre: this.form.nombre.trim(),
          descripcion: this.form.descripcion.trim() || '',
          icono: this.form.icono || 'mdi:folder-outline',
          ruta: this.form.ruta.trim(),
          estado: this.form.estado || 'Activo',
          tipo: tipoFinal,
          orden: this.form.orden ?? 0
        };

        console.log('📤 Enviando módulo:', moduloData);

        let result;
        if (this.editingModulo) {
          result = await actualizarModulo(this.editingModulo.idModulo, moduloData);
        } else {
          result = await crearModulo(moduloData);
        }

        if (result.success) {
          await this.loadModulos();
          this.closeModal();
          this.toast.success(
            this.editingModulo ? 'Módulo actualizado correctamente' : 'Módulo creado correctamente'
          );
        } else {
          throw new Error(result.error || 'Error al guardar');
        }
      } catch (error) {
        console.error('❌ Error al guardar módulo:', error);
        this.toast.error(error.message || 'Error al guardar el módulo');
      } finally {
        this.saving = false;
      }
    },

    async confirmDelete(modulo) {
      const tieneHijos = this.modulos.some(m => m.idPadre === modulo.idModulo);
      if (tieneHijos) {
        this.toast.error('No se puede eliminar: el módulo tiene submódulos. Elimínalos primero.');
        return;
      }

      const permisosResult = await verificarModuloTienePermisos(modulo.idModulo);

      let mensaje = `¿Estás seguro de eliminar el módulo <strong>"${modulo.nombre}"</strong>?`;
      let detalle = 'Esta acción no se puede deshacer.';

      if (permisosResult.success && permisosResult.tienePermisos) {
        detalle = '⚠️ ADVERTENCIA: Este módulo tiene permisos asignados. Eliminarlo también eliminará todos los permisos asociados.';
      }

      const confirmed = await this.showConfirm({
        title: '¿Eliminar módulo?',
        message: mensaje,
        detail: detalle,
        confirmText: 'Sí, eliminar',
        cancelText: 'Cancelar',
        type: 'danger'
      });

      if (confirmed) {
        await this.deleteModulo(modulo.idModulo, modulo.nombre);
      }
    },

    async deleteModulo(id, nombre) {
      try {
        const result = await eliminarModulo(id);
        if (result.success) {
          await this.loadModulos();
          this.toast.success(`El módulo "${nombre}" ha sido eliminado`);
        } else {
          throw new Error(result.error || 'Error al eliminar');
        }
      } catch (error) {
        console.error('❌ Error al eliminar módulo:', error);
        this.toast.error(error.message || 'Error al eliminar el módulo');
      }
    },

    async toggleStatus(modulo) {
      const esActivo = modulo.estado?.toLowerCase() === 'activo';

      let mensajeAdvertencia = '';
      if (esActivo) {
        const permisosResult = await verificarModuloTienePermisos(modulo.idModulo);
        if (permisosResult.success && permisosResult.tienePermisos) {
          mensajeAdvertencia = ' Este módulo tiene permisos asignados. Al desactivarlo, los permisos quedarán inactivos.';
        }
      }

      const confirmed = await this.showConfirm({
        title: `${!esActivo ? 'Activar' : 'Desactivar'} módulo`,
        message: `¿Deseas ${!esActivo ? 'activar' : 'desactivar'} el módulo <strong>"${modulo.nombre}"</strong>?${mensajeAdvertencia}`,
        confirmText: `Sí, ${!esActivo ? 'activar' : 'desactivar'}`,
        cancelText: 'Cancelar',
        type: 'warning'
      });

      if (confirmed) {
        try {
          const nuevoEstado = !esActivo ? 'Activo' : 'Inactivo';
          const result = await cambiarEstadoModulo({
            idModulo: modulo.idModulo,
            estado: nuevoEstado
          });

          if (result.success) {
            await this.loadModulos();
            this.toast.success(`El módulo ha sido ${!esActivo ? 'activado' : 'desactivado'} correctamente`);
          } else {
            throw new Error(result.error || 'Error al cambiar estado');
          }
        } catch (error) {
          console.error('❌ Error al cambiar estado:', error);
          this.toast.error('Error al cambiar el estado del módulo');
        }
      }
    },

    async verificarPermisosModulo(idModulo) {
      try {
        const result = await verificarModuloTienePermisos(idModulo);
        if (result.success) {
          if (result.tienePermisos) {
            this.toast.info('Este módulo tiene permisos asignados a roles específicos');
          } else {
            this.toast.info('Este módulo no tiene permisos asignados');
          }
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error('❌ Error verificando permisos:', error);
        this.toast.error('Error al verificar permisos del módulo');
      }
    },

    async loadIconosDisponibles() {
      try {
        const result = await obtenerIconosDisponibles();
        if (result.success && result.data && result.data.length > 0) {
          const iconosSugeridos = result.data.slice(0, 10).join(', ');
          this.toast.info(`Iconos disponibles: ${iconosSugeridos}...`, { timeout: 5000 });
        } else {
          this.toast.info('Iconos comunes: mdi:home, mdi:cart, mdi:account, mdi:cog, mdi:chart-bar');
        }
      } catch (error) {
        console.error('❌ Error cargando iconos:', error);
        this.toast.info('Iconos comunes: mdi:home, mdi:cart, mdi:account, mdi:cog, mdi:chart-bar');
      }
    },

    printFullReport() {
      if (this.filteredModulos.length === 0) {
        this.toast.warning('No hay datos para imprimir');
        return;
      }
      try {
        const filtros = {
          estado: this.filterStatus,
          tipo: this.filterTipo,
          busqueda: this.searchQuery,
          orden: this.sortBy
        };
        imprimirReporteModulos(this.filteredModulos, 'REPORTE DE MÓDULOS - SISTEMA', filtros);
        this.showExportMenu = false;
      } catch (error) {
        console.error('❌ Error generando reporte:', error);
        this.toast.error('Error al generar el reporte: ' + error.message);
      }
    },

    printSimplifiedReport() {
      if (this.filteredModulos.length === 0) {
        this.toast.warning('No hay datos para imprimir');
        return;
      }
      try {
        imprimirReporteSimplificado(this.filteredModulos, 'LISTADO DE MÓDULOS - SISTEMA');
        this.showExportMenu = false;
      } catch (error) {
        console.error('❌ Error generando reporte:', error);
        this.toast.error('Error al generar el reporte: ' + error.message);
      }
    },

    printCurrentView() {
      this.printFullReport();
    },

    clearFilters() {
      this.searchQuery = '';
      this.filterStatus = 'all';
      this.filterTipo = 'all';
      this.sortBy = 'orden';
      this.currentPage = 1;
      this.loadModulos();
    },

    formatDate(fechaISO) {
      if (!fechaISO) return '';
      try {
        return new Date(fechaISO).toLocaleDateString('es-ES', {
          year: 'numeric', month: 'short', day: 'numeric'
        });
      } catch { return fechaISO; }
    },

    formatTime(fechaISO) {
      if (!fechaISO) return '';
      try {
        return new Date(fechaISO).toLocaleTimeString('es-ES', {
          hour: '2-digit', minute: '2-digit'
        });
      } catch { return ''; }
    },

    // =====================================================
    // ORGANIZADOR DEL MENÚ Y SUBMÓDULOS (MUÑECA RUSA)
    // =====================================================
    contarHijos(idModulo) {
      if (!idModulo || !Array.isArray(this.modulos)) return 0;
      return this.modulos.filter(m => m.idPadre === idModulo).length;
    },

    openOrganizarOrdenModal() {
      this.tabOrganizar = 'raices';
      this.modulosRaizOrganizar = this.modulos
        .filter(m => !m.idPadre)
        .map(m => ({ ...m }))
        .sort((a, b) => (a.orden ?? 999) - (b.orden ?? 999));

      this.modulosRaizOrganizar.forEach((m, i) => {
        if (m.orden === undefined || m.orden === null) m.orden = i + 1;
      });

      const primerPadre = this.modulosPadreDisponibles[0];
      this.padreSeleccionadoId = primerPadre ? primerPadre.idModulo : null;
      this.cargarSubmodulosDelPadre();

      this.showOrganizarModal = true;
    },

    closeOrganizarModal() {
      this.showOrganizarModal = false;
    },

    seleccionarPadreParaOrganizar(idPadre) {
      this.padreSeleccionadoId = idPadre;
      this.cargarSubmodulosDelPadre();
      this.tabOrganizar = 'submodulos';
    },

    cargarSubmodulosDelPadre() {
      if (!this.padreSeleccionadoId) {
        this.submodulosDelPadre = [];
        return;
      }
      this.submodulosDelPadre = this.modulos
        .filter(m => m.idPadre === this.padreSeleccionadoId)
        .map(m => ({ ...m }))
        .sort((a, b) => (a.orden ?? 999) - (b.orden ?? 999));

      this.submodulosDelPadre.forEach((m, i) => {
        if (m.orden === undefined || m.orden === null) m.orden = i + 1;
      });
    },

    moverEnListaRaices(index, delta) {
      const targetIndex = index + delta;
      if (targetIndex < 0 || targetIndex >= this.modulosRaizOrganizar.length) return;
      const item = this.modulosRaizOrganizar.splice(index, 1)[0];
      this.modulosRaizOrganizar.splice(targetIndex, 0, item);
      this.modulosRaizOrganizar.forEach((m, i) => {
        m.orden = i + 1;
      });
    },

    moverEnListaSubmodulos(index, delta) {
      const targetIndex = index + delta;
      if (targetIndex < 0 || targetIndex >= this.submodulosDelPadre.length) return;
      const item = this.submodulosDelPadre.splice(index, 1)[0];
      this.submodulosDelPadre.splice(targetIndex, 0, item);
      this.submodulosDelPadre.forEach((m, i) => {
        m.orden = i + 1;
      });
    },

    async moverOrdenRapido(modulo, delta) {
      try {
        const hermanos = this.modulos
          .filter(m => m.idPadre === modulo.idPadre)
          .sort((a, b) => (a.orden ?? 999) - (b.orden ?? 999));

        const idx = hermanos.findIndex(m => m.idModulo === modulo.idModulo);
        if (idx === -1) return;
        const targetIdx = idx + delta;
        if (targetIdx < 0 || targetIdx >= hermanos.length) return;

        const hermanoVecino = hermanos[targetIdx];
        const ordenActual = modulo.orden ?? (idx + 1);
        const ordenVecino = hermanoVecino.orden ?? (targetIdx + 1);

        const nuevoOrdenModulo = ordenVecino === ordenActual ? (delta > 0 ? ordenActual + 1 : Math.max(1, ordenActual - 1)) : ordenVecino;
        const nuevoOrdenVecino = ordenActual;

        await actualizarModulo(modulo.idModulo, {
          ...modulo,
          orden: nuevoOrdenModulo
        });

        await actualizarModulo(hermanoVecino.idModulo, {
          ...hermanoVecino,
          orden: nuevoOrdenVecino
        });

        this.toast.success(`Orden cambiado: "${modulo.nombre}" (#${nuevoOrdenModulo})`);
        await this.loadModulos();
        window.dispatchEvent(new CustomEvent('menuUpdated'));
        window.dispatchEvent(new CustomEvent('userUpdated'));
      } catch (error) {
        console.error('Error al mover orden:', error);
        this.toast.error('No se pudo actualizar el orden');
      }
    },

    async guardarOrdenCompleto() {
      this.guardandoOrden = true;
      try {
        for (const mod of this.modulosRaizOrganizar) {
          const original = this.modulos.find(m => m.idModulo === mod.idModulo);
          if (original && original.orden !== mod.orden) {
            await actualizarModulo(mod.idModulo, {
              ...original,
              orden: Number(mod.orden)
            });
          }
        }

        for (const sub of this.submodulosDelPadre) {
          const original = this.modulos.find(m => m.idModulo === sub.idModulo);
          if (original && original.orden !== sub.orden) {
            await actualizarModulo(sub.idModulo, {
              ...original,
              orden: Number(sub.orden)
            });
          }
        }

        this.toast.success('¡Orden del menú guardado y actualizado exitosamente!');
        await this.loadModulos();
        this.closeOrganizarModal();
        window.dispatchEvent(new CustomEvent('menuUpdated'));
        window.dispatchEvent(new CustomEvent('userUpdated'));
      } catch (error) {
        console.error('Error guardando orden:', error);
        this.toast.error('Ocurrió un error al guardar el orden');
      } finally {
        this.guardandoOrden = false;
      }
    },
  },
};
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.fade-in { animation: fadeIn 0.3s ease-out; }

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

::-webkit-scrollbar { width: 8px; height: 8px; }
::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
::-webkit-scrollbar-thumb { background: #888; border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: #555; }

.dark ::-webkit-scrollbar-track { background: #374151; }
.dark ::-webkit-scrollbar-thumb { background: #6b7280; }
.dark ::-webkit-scrollbar-thumb:hover { background: #9ca3af; }
</style>